import Phaser from "phaser";
import {
  ANIMATION_FRAME_RATES,
  ANIMATION_KEYS,
  CHASE_OFFSETS,
  DIRECTIONS,
  GAME_CONFIG,
  GAME_MODES,
  GHOST_HOUSE,
  GHOST_SCATTER_TARGETS,
  GHOST_START_POSITIONS,
  GHOST_TEXTURES,
  MODE_DURATIONS,
  RENDER_DEPTHS,
  SOUND_KEYS,
  SPRITE_DIMENSIONS,
  TIMING,
} from "../../constants/game";
import { Ghost, Intersection, Point } from "../../types/game";
import { SoundManager } from "../SoundManager";

/**
 * GhostAI Manager
 * Handles all ghost AI, behavior, movement, and pathfinding logic
 */
export class GhostAI {
  private scene: Phaser.Scene;
  private blockSize: number;
  private speed: number;
  private ghostSpeed: number;
  private currentMode: string = GAME_MODES.SCATTER;
  private previousMode?: string;
  private modeTimer: ReturnType<typeof setTimeout> | null = null;
  private modeTimerStartTime: number = 0;
  private modeTimerDuration: number = 0;
  private isPaused: boolean = false;
  // Track ghost entry timers for pause/resume
  private ghostEntryTimers: Map<
    Ghost,
    { startTime: number; duration: number }
  > = new Map();

  // Mode durations
  private scatterModeDuration: number = MODE_DURATIONS.SCATTER_MODE;
  private chaseModeDuration: number = MODE_DURATIONS.CHASE_MODE;
  private scaredModeDuration: number = MODE_DURATIONS.SCARED_MODE;
  private entryDelay: number = MODE_DURATIONS.ENTRY_DELAY;

  // References
  private pacman!: Phaser.Physics.Arcade.Sprite;
  private ghosts: Ghost[] = [];
  private direction: string;
  private getIntersections!: () => Intersection[];
  private aStarPathfinding!: (start: Point, target: Point) => Point[];
  private soundManager?: SoundManager;

  constructor(
    scene: Phaser.Scene,
    blockSize: number,
    speed: number,
    pacmanDirection: string
  ) {
    this.scene = scene;
    this.blockSize = blockSize;
    this.speed = speed;
    this.ghostSpeed = this.speed * GAME_CONFIG.GHOST_SPEED_MULTIPLIER;
    this.direction = pacmanDirection;
  }

  /**
   * Set references needed for ghost AI
   */
  setReferences(
    pacman: Phaser.Physics.Arcade.Sprite,
    ghosts: Ghost[],
    getIntersections: () => Intersection[],
    aStarPathfinding: (start: Point, target: Point) => Point[],
    soundManager?: SoundManager
  ): void {
    this.pacman = pacman;
    this.ghosts = ghosts;
    this.getIntersections = getIntersections;
    this.aStarPathfinding = aStarPathfinding;
    this.soundManager = soundManager;
  }

  /**
   * Update Pacman direction for chase AI
   */
  updatePacmanDirection(direction: string): void {
    this.direction = direction;
  }

  /**
   * Initialize mode timers
   */
  initModeTimers(): void {
    this.setModeTimer(this.scatterModeDuration);
  }

  /**
   * Set a timer for the current game mode
   */
  setModeTimer(duration: number): void {
    if (this.modeTimer) {
      clearTimeout(this.modeTimer);
    }
    this.modeTimerDuration = duration;
    this.modeTimerStartTime = Date.now();
    this.modeTimer = setTimeout(() => {
      this.switchMode();
    }, duration);
  }

  /**
   * Switch between different game modes (scatter, chase, scared)
   */
  switchMode(): void {
    if (this.currentMode === GAME_MODES.SCARED) {
      // Switch to normal mode
      if (this.soundManager) {
        this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_NORMAL_MOVE);
      }

      this.currentMode = this.previousMode || GAME_MODES.SCATTER;
      this.setModeTimer(
        this.currentMode === GAME_MODES.SCATTER
          ? this.scatterModeDuration
          : this.chaseModeDuration
      );
      this.ghostSpeed = this.speed * GAME_CONFIG.GHOST_SPEED_MULTIPLIER;
      this.ghosts.forEach((ghost) => {
        if (ghost.blinkInterval) clearInterval(ghost.blinkInterval);
        ghost.setTexture(ghost.originalTexture);
        const target =
          this.currentMode === GAME_MODES.CHASE
            ? this.getChaseTarget(ghost)
            : this.getScatterTarget(ghost);
        this.updateGhostPath(ghost, target);
        ghost.hasBeenEaten = true;
      });
    } else {
      if (this.currentMode === GAME_MODES.SCATTER) {
        this.currentMode = GAME_MODES.CHASE;
        this.setModeTimer(this.chaseModeDuration);
      } else {
        this.currentMode = GAME_MODES.SCATTER;
        this.setModeTimer(this.scatterModeDuration);
      }
      this.ghosts.forEach((ghost) => {
        const target =
          this.currentMode === GAME_MODES.CHASE
            ? this.getChaseTarget(ghost)
            : this.getScatterTarget(ghost);
        this.updateGhostPath(ghost, target);
      });
      this.previousMode = this.currentMode;
    }
  }

  /**
   * Get the current game mode
   */
  getCurrentMode(): string {
    return this.currentMode;
  }

  /**
   * Calculate the chase target for a specific ghost based on its AI behavior
   */
  getChaseTarget(ghost: Ghost): Point {
    if (ghost.texture.key === GHOST_TEXTURES.RED) {
      return { x: this.pacman.x, y: this.pacman.y };
    }
    if (ghost.texture.key === GHOST_TEXTURES.PINK) {
      const offset = this.blockSize * CHASE_OFFSETS.PINKY_AHEAD;
      switch (this.direction) {
        case DIRECTIONS.RIGHT:
          return { x: this.pacman.x + offset, y: this.pacman.y };
        case DIRECTIONS.LEFT:
          return { x: this.pacman.x - offset, y: this.pacman.y };
        case DIRECTIONS.UP:
          return { x: this.pacman.x, y: this.pacman.y - offset };
        case DIRECTIONS.DOWN:
          return { x: this.pacman.x, y: this.pacman.y + offset };
        default:
          return { x: this.pacman.x, y: this.pacman.y };
      }
    }
    if (ghost.texture.key === GHOST_TEXTURES.ORANGE) {
      const distance = Math.hypot(
        ghost.x - this.pacman.x,
        ghost.y - this.pacman.y
      );
      return distance > this.blockSize * CHASE_OFFSETS.CLYDE_DISTANCE_THRESHOLD
        ? { x: this.pacman.x, y: this.pacman.y }
        : GHOST_SCATTER_TARGETS.CLYDE;
    }
    if (ghost.texture.key === GHOST_TEXTURES.BLUE) {
      const redGhost = this.ghosts.find(
        (g) => g.texture.key === GHOST_TEXTURES.RED
      );
      if (!redGhost) return { x: 0, y: 0 };

      let pacmanAhead: Point = { x: this.pacman.x, y: this.pacman.y };
      const aheadOffset = this.blockSize * CHASE_OFFSETS.INKY_AHEAD;
      switch (this.direction) {
        case DIRECTIONS.RIGHT:
          pacmanAhead = { x: this.pacman.x + aheadOffset, y: this.pacman.y };
          break;
        case DIRECTIONS.LEFT:
          pacmanAhead = { x: this.pacman.x - aheadOffset, y: this.pacman.y };
          break;
        case DIRECTIONS.UP:
          pacmanAhead = { x: this.pacman.x, y: this.pacman.y - aheadOffset };
          break;
        case DIRECTIONS.DOWN:
          pacmanAhead = { x: this.pacman.x, y: this.pacman.y + aheadOffset };
          break;
      }
      const vectorX = pacmanAhead.x - redGhost.x;
      const vectorY = pacmanAhead.y - redGhost.y;
      return { x: redGhost.x + 2 * vectorX, y: redGhost.y + 2 * vectorY };
    }
    return { x: 0, y: 0 };
  }

  /**
   * Get a random target for scared ghosts
   */
  getScaredTarget(): Point {
    const intersections = this.getIntersections();
    const randomIndex = Math.floor(Math.random() * intersections.length);
    const randomIntersection = intersections[randomIndex];
    return { x: randomIntersection.x, y: randomIntersection.y };
  }

  /**
   * Get the scatter target for a specific ghost
   */
  getScatterTarget(ghost: Ghost): Point {
    if (ghost.texture.key === GHOST_TEXTURES.RED)
      return GHOST_SCATTER_TARGETS.BLINKY;
    if (ghost.texture.key === GHOST_TEXTURES.PINK) {
      return GHOST_SCATTER_TARGETS.PINKY;
    }
    if (ghost.texture.key === GHOST_TEXTURES.ORANGE)
      return GHOST_SCATTER_TARGETS.CLYDE;
    if (ghost.texture.key === GHOST_TEXTURES.BLUE)
      return GHOST_SCATTER_TARGETS.INKY;
    return GHOST_SCATTER_TARGETS.BLINKY;
  }

  /**
   * Update a ghost's path using A* algorithm
   */
  updateGhostPath(ghost: Ghost, chaseTarget: Point): void {
    let chaseStartPoint: Point = { x: ghost.x, y: ghost.y };

    // If ghost is in ghost house, use entrance as starting point for pathfinding
    if (this.isInGhostHouse(ghost.x, ghost.y)) {
      chaseStartPoint = {
        x: GHOST_HOUSE.ENTRANCE_X,
        y: GHOST_HOUSE.ENTRANCE_Y,
      };
    }

    ghost.path = this.aStarPathfinding(chaseStartPoint, chaseTarget);
    if (ghost.path.length > 0) {
      ghost.nextIntersection = ghost.path.shift() ?? null;
    }
  }

  /**
   * Initialize a single ghost
   */
  initializeGhost(
    x: number,
    y: number,
    spriteKey: string,
    layer: Phaser.Tilemaps.TilemapLayer
  ): Ghost {
    const ghost = this.scene.physics.add.sprite(x, y, spriteKey) as Ghost;
    ghost.setDepth(RENDER_DEPTHS.GHOSTS);
    ghost.setDisplaySize(
      SPRITE_DIMENSIONS.FRAME_WIDTH,
      SPRITE_DIMENSIONS.FRAME_HEIGHT
    );
    this.scene.physics.add.collider(ghost, layer);
    ghost.originalTexture = spriteKey;
    ghost.direction = DIRECTIONS.RIGHT;
    ghost.previousDirection = DIRECTIONS.RIGHT;
    ghost.nextIntersection = null;
    ghost.enteredMaze = false;
    ghost.hasBeenEaten = true;
    ghost.path = [];
    return ghost;
  }

  /**
   * Initialize all ghosts
   */
  initializeAllGhosts(layer: Phaser.Tilemaps.TilemapLayer): Ghost[] {
    const pinkGhost = this.initializeGhost(
      GHOST_START_POSITIONS.PINK.x,
      GHOST_START_POSITIONS.PINK.y,
      GHOST_TEXTURES.PINK,
      layer
    );
    const orangeGhost = this.initializeGhost(
      GHOST_START_POSITIONS.ORANGE.x,
      GHOST_START_POSITIONS.ORANGE.y,
      GHOST_TEXTURES.ORANGE,
      layer
    );
    const redGhost = this.initializeGhost(
      GHOST_START_POSITIONS.RED.x,
      GHOST_START_POSITIONS.RED.y,
      GHOST_TEXTURES.RED,
      layer
    );
    const blueGhost = this.initializeGhost(
      GHOST_START_POSITIONS.BLUE.x,
      GHOST_START_POSITIONS.BLUE.y,
      GHOST_TEXTURES.BLUE,
      layer
    );

    // Set animations
    // pinkGhost.play(ANIMATION_KEYS.PINK_GHOST_ANIM);
    // redGhost.play(ANIMATION_KEYS.RED_GHOST_ANIM);
    // orangeGhost.play(ANIMATION_KEYS.ORANGE_GHOST_ANIM);
    // blueGhost.play(ANIMATION_KEYS.BLUE_GHOST_ANIM);

    this.ghosts = [pinkGhost, redGhost, orangeGhost, blueGhost];
    return this.ghosts;
  }

  /**
   * Start staggered ghost entries into maze
   */
  startGhostEntries(): void {
    this.ghosts.forEach((ghost, index) => {
      // Skip if ghost has already entered the maze
      if (ghost.enteredMaze) {
        return;
      }

      if (ghost.entryTimer) {
        clearTimeout(ghost.entryTimer);
      }

      const delay = this.entryDelay * index;
      const startTime = Date.now();

      // Track entry timer info for pause/resume
      this.ghostEntryTimers.set(ghost, {
        startTime,
        duration: delay,
      });

      ghost.entryTimer = setTimeout(() => {
        this.enterMaze(ghost);
        // Remove from tracking once entry is complete
        this.ghostEntryTimers.delete(ghost);
      }, delay);
    });
  }

  /**
   * Move a ghost from the ghost house into the main maze
   */
  enterMaze(ghost: Ghost): void {
    ghost.setPosition(GHOST_HOUSE.ENTRANCE_X, GHOST_HOUSE.ENTRANCE_Y);
    ghost.enteredMaze = true;

    // If currently in scared mode, turn the ghost scared as they exit
    if (this.currentMode === GAME_MODES.SCARED) {
      ghost.setTexture(GHOST_TEXTURES.SCARED);
      ghost.hasBeenEaten = false;

      // Set up blinking for this ghost
      if (ghost.blinkInterval) clearInterval(ghost.blinkInterval);

      const blinkTime = this.scaredModeDuration - TIMING.BLINK_START_DELAY;
      ghost.blinkInterval = setTimeout(() => {
        if (ghost.hasBeenEaten) return;

        let blinkOn = true;
        ghost.blinkInterval = setInterval(() => {
          blinkOn = !blinkOn;
          ghost.setTexture(
            blinkOn ? GHOST_TEXTURES.SCARED : GHOST_TEXTURES.SCARED_WHITE
          );
        }, ANIMATION_FRAME_RATES.GHOST_BLINK);
      }, blinkTime);
    } else {
      ghost.hasBeenEaten = true;
    }
  }

  /**
   * Set ghosts to scared mode
   */
  setGhostsToScaredMode(): void {
    // This prevents previousMode from being set to SCARED when collecting multiple power pills
    if (this.currentMode !== GAME_MODES.SCARED) {
      this.previousMode = this.currentMode;
    }

    this.currentMode = GAME_MODES.SCARED;
    this.ghostSpeed = this.speed * GAME_CONFIG.SCARED_GHOST_SPEED_MULTIPLIER;
    this.setModeTimer(this.scaredModeDuration);

    // Play scared mode sound
    if (this.soundManager) {
      this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_SCARED_MODE);
    }

    // Update ghosts - but only change appearance of those NOT in ghost house
    this.ghosts.forEach((ghost) => {
      // Skip ghosts that are in the ghost house - they should keep their normal appearance
      const isInHouse = this.isInGhostHouse(ghost.x, ghost.y);

      if (!isInHouse) {
        const scaredTarget = this.getScaredTarget();
        this.updateGhostPath(ghost, scaredTarget);

        // Clear existing blink interval before setting new one
        if (ghost.blinkInterval) clearInterval(ghost.blinkInterval);

        const blinkTime = this.scaredModeDuration - TIMING.BLINK_START_DELAY;
        ghost.blinkInterval = setTimeout(() => {
          if (ghost.hasBeenEaten) return;

          let blinkOn = true;
          ghost.blinkInterval = setInterval(() => {
            blinkOn = !blinkOn;
            ghost.setTexture(
              blinkOn ? GHOST_TEXTURES.SCARED : GHOST_TEXTURES.SCARED_WHITE
            );
          }, ANIMATION_FRAME_RATES.GHOST_BLINK);
        }, blinkTime);

        ghost.setTexture(GHOST_TEXTURES.SCARED);

        ghost.hasBeenEaten = false;
      } else {
        // Ghost is in house - keep normal texture but mark as vulnerable when they exit
        ghost.hasBeenEaten = false;
      }
    });
  }

  /**
   * Handle ghost movement
   */
  handleGhostMovement(ghost: Ghost): void {
    let nextIntersectionx = 0;
    let nextIntersectiony = 0;
    if (ghost.nextIntersection) {
      nextIntersectionx = ghost.nextIntersection.x;
      nextIntersectiony = ghost.nextIntersection.y;
    }
    switch (ghost.direction) {
      case DIRECTIONS.LEFT:
        this.handleGhostMovementInDirection(
          ghost,
          DIRECTIONS.LEFT,
          DIRECTIONS.RIGHT,
          ghost.y,
          nextIntersectiony,
          ghost.x,
          -this.ghostSpeed,
          0,
          ghost.body?.velocity.y
        );
        break;
      case DIRECTIONS.RIGHT:
        this.handleGhostMovementInDirection(
          ghost,
          DIRECTIONS.RIGHT,
          DIRECTIONS.LEFT,
          ghost.y,
          nextIntersectiony,
          ghost.x,
          this.ghostSpeed,
          0,
          ghost.body?.velocity.y
        );
        break;
      case DIRECTIONS.UP:
        this.handleGhostMovementInDirection(
          ghost,
          DIRECTIONS.UP,
          DIRECTIONS.DOWN,
          ghost.x,
          nextIntersectionx,
          ghost.y,
          0,
          -this.ghostSpeed,
          ghost.body?.velocity.x
        );
        break;
      case DIRECTIONS.DOWN:
        this.handleGhostMovementInDirection(
          ghost,
          DIRECTIONS.DOWN,
          DIRECTIONS.UP,
          ghost.x,
          nextIntersectionx,
          ghost.y,
          0,
          this.ghostSpeed,
          ghost.body?.velocity.x
        );
        break;
    }
  }

  /**
   * Handle ghost movement logic for a specific direction
   */
  private handleGhostMovementInDirection(
    ghost: Ghost,
    currentDirection: string,
    oppositeDirection: string,
    ghostPosition: number,
    intersectionPosition: number = 0,
    movingCoordinate: number,
    velocityX: number,
    velocityY: number,
    currentVelocity?: number
  ): void {
    const perpendicularDirection =
      currentDirection === DIRECTIONS.LEFT ||
      currentDirection === DIRECTIONS.RIGHT
        ? [DIRECTIONS.UP, DIRECTIONS.DOWN]
        : [DIRECTIONS.LEFT, DIRECTIONS.RIGHT];
    let condition = false;
    if (ghost.nextIntersection)
      condition =
        (ghost.previousDirection == perpendicularDirection[0] &&
          ghostPosition <= intersectionPosition) ||
        (ghost.previousDirection == perpendicularDirection[1] &&
          ghostPosition >= intersectionPosition) ||
        ghost.previousDirection === oppositeDirection;
    if (condition) {
      const newPosition = intersectionPosition;
      if (
        ghost.previousDirection != oppositeDirection &&
        newPosition !== ghostPosition
      ) {
        if (
          currentDirection === DIRECTIONS.LEFT ||
          currentDirection === DIRECTIONS.RIGHT
        )
          ghost.body?.reset(movingCoordinate, newPosition);
        else ghost.body?.reset(newPosition, movingCoordinate);
      }
      this.changeGhostDirection(ghost, velocityX, velocityY);
    } else if (currentVelocity === 0) {
      this.changeGhostDirection(ghost, velocityX, velocityY);
    }
  }

  /**
   * Apply velocity changes to a ghost when changing direction
   */
  private changeGhostDirection(
    ghost: Ghost,
    velocityX: number,
    velocityY: number
  ): void {
    ghost.setVelocityY(velocityY);
    ghost.setVelocityX(velocityX);
  }

  /**
   * Handle ghost direction changes and pathfinding logic
   */
  handleGhostDirection(ghost: Ghost): void {
    if (this.isInGhostHouse(ghost.x, ghost.y)) {
      this.changeGhostDirection(ghost, 0, -this.ghostSpeed);
      if (ghost.direction === DIRECTIONS.DOWN) ghost.direction = DIRECTIONS.UP;
    }

    const isMoving =
      ghost.body?.velocity.x !== 0 || ghost.body?.velocity.y !== 0;
    if (!isMoving) {
      ghost.stuckTimer = (ghost.stuckTimer || 0) + 1;
      if (ghost.stuckTimer > TIMING.STUCK_TIMER_THRESHOLD) {
        ghost.stuckTimer = 0;
        const newTarget =
          this.currentMode === GAME_MODES.SCARED
            ? this.getScaredTarget()
            : this.currentMode === GAME_MODES.CHASE
              ? this.getChaseTarget(ghost)
              : this.getScatterTarget(ghost);
        this.updateGhostPath(ghost, newTarget);
      }
    } else ghost.stuckTimer = 0;

    if (ghost.body?.velocity.x == 0 && ghost.body?.velocity.y == 0) {
      this.adjustGhostPosition(ghost);
    }

    const isAtIntersection = this.isGhostAtIntersection(
      ghost.nextIntersection ?? { x: 0, y: 0 },
      ghost.x,
      ghost.y,
      ghost.direction
    );

    if (isAtIntersection) {
      if (
        GHOST_SCATTER_TARGETS.PINKY.x === ghost.nextIntersection?.x &&
        GHOST_SCATTER_TARGETS.PINKY.y === ghost.nextIntersection.y &&
        this.currentMode === GAME_MODES.SCATTER &&
        ghost.texture.key === GHOST_TEXTURES.PINK
      )
        return;
      if (
        GHOST_SCATTER_TARGETS.BLINKY.x === ghost.nextIntersection?.x &&
        GHOST_SCATTER_TARGETS.BLINKY.y === ghost.nextIntersection.y &&
        this.currentMode === GAME_MODES.SCATTER &&
        ghost.texture.key === GHOST_TEXTURES.RED
      )
        return;
      if (
        GHOST_SCATTER_TARGETS.INKY.x === ghost.nextIntersection?.x &&
        GHOST_SCATTER_TARGETS.INKY.y === ghost.nextIntersection.y &&
        this.currentMode === GAME_MODES.SCATTER &&
        ghost.texture.key === GHOST_TEXTURES.BLUE
      )
        return;
      if (
        GHOST_SCATTER_TARGETS.CLYDE.x === ghost.nextIntersection?.x &&
        GHOST_SCATTER_TARGETS.CLYDE.y === ghost.nextIntersection.y &&
        this.currentMode === GAME_MODES.SCATTER &&
        ghost.texture.key === GHOST_TEXTURES.ORANGE
      )
        return;

      if (this.currentMode === GAME_MODES.CHASE) {
        const chaseTarget = this.getChaseTarget(ghost);
        this.updateGhostPath(ghost, chaseTarget);
      }

      if (ghost.path.length > 0) {
        ghost.nextIntersection = ghost.path.shift() ?? null;
      }
      if (ghost.path.length == 0 && this.currentMode === GAME_MODES.SCARED) {
        const scaredTarget = this.getScaredTarget();
        this.updateGhostPath(ghost, scaredTarget);
      }

      const newDirection = this.getGhostNextDirection(
        ghost,
        ghost.nextIntersection ?? { x: 0, y: 0 }
      );
      ghost.previousDirection = ghost.direction;
      ghost.direction = newDirection;
    }
  }

  /**
   * Adjust ghost position to align with the grid
   */
  private adjustGhostPosition(ghost: Ghost): void {
    if (ghost.x % this.blockSize !== 0) {
      const nearestMultiple =
        Math.round(ghost.x / this.blockSize) * this.blockSize;
      ghost.body?.reset(nearestMultiple, ghost.y);
    }
    if (ghost.y % this.blockSize !== 0) {
      const nearestMultiple =
        Math.round(ghost.y / this.blockSize) * this.blockSize;
      ghost.body?.reset(ghost.x, nearestMultiple);
    }
  }

  /**
   * Update animations for all ghosts
   * Public method to be called from the scene
   */
  updateAllGhostAnimations(): void {
    this.ghosts.forEach((ghost) => {
      this.updateGhostAnimation(ghost);
    });
  }

  /**
   * Get the animation key for a ghost based on its texture
   */
  private getGhostAnimationKey(ghost: Ghost): string | null {
    if (ghost.texture.key === GHOST_TEXTURES.PINK) {
      return ANIMATION_KEYS.PINK_GHOST_ANIM;
    }
    if (ghost.texture.key === GHOST_TEXTURES.ORANGE) {
      return ANIMATION_KEYS.ORANGE_GHOST_ANIM;
    }
    if (ghost.texture.key === GHOST_TEXTURES.RED) {
      return ANIMATION_KEYS.RED_GHOST_ANIM;
    }
    if (ghost.texture.key === GHOST_TEXTURES.BLUE) {
      return ANIMATION_KEYS.BLUE_GHOST_ANIM;
    }
    return null;
  }

  /**
   * Update ghost animation based on current mode and texture
   * In scared mode, animations are stopped (texture changes handle visual)
   * When in ghost house, animations are stopped
   */
  private updateGhostAnimation(ghost: Ghost): void {
    try {
      // If ghost is in the ghost house, stop animations
      // if (this.isInGhostHouse(ghost.x, ghost.y)) {
      //   ghost.anims.stop();
      //   return;
      // }

      // // If in scared mode, stop animations (texture changes handle the visual)
      // if (this.currentMode === GAME_MODES.SCARED && !ghost.hasBeenEaten) {
      //   ghost.anims.stop();
      //   return;
      // }

      // For normal modes, play the appropriate animation
      const animationKey = this.getGhostAnimationKey(ghost);
      if (animationKey && ghost.texture.key === ghost.originalTexture) {
        // Only play animation if using original texture (not scared)
        if (
          !ghost.anims.isPlaying ||
          ghost.anims.currentAnim?.key !== animationKey
        ) {
          ghost.play(animationKey);
        }
      }
    } catch (error) {
      // Silently handle any animation errors to prevent movement issues
      console.warn("Error updating ghost animation:", error);
    }
  }
  /**
   * Check if a ghost has reached its target intersection
   */
  private isGhostAtIntersection(
    intersection: Point,
    currentX: number,
    currentY: number,
    direction: string
  ): boolean {
    const isUp = direction === DIRECTIONS.UP;
    const isDown = direction === DIRECTIONS.DOWN;
    const isLeft = direction === DIRECTIONS.LEFT;
    const isRight = direction === DIRECTIONS.RIGHT;

    const condition =
      (isUp && intersection.x === currentX && intersection.y >= currentY) ||
      (isDown && intersection.x === currentX && intersection.y <= currentY) ||
      (isLeft && intersection.y === currentY && intersection.x >= currentX) ||
      (isRight && intersection.y === currentY && intersection.x <= currentX);
    return condition;
  }

  /**
   * Determine the next direction for a ghost to reach its target intersection
   */
  private getGhostNextDirection(ghost: Ghost, intersection: Point): string {
    if (
      Math.abs(intersection.x - ghost.x) < this.blockSize &&
      ghost.y <= intersection.y
    )
      return DIRECTIONS.DOWN;
    if (
      Math.abs(intersection.x - ghost.x) < this.blockSize &&
      ghost.y >= intersection.y
    )
      return DIRECTIONS.UP;
    if (
      Math.abs(intersection.y - ghost.y) < this.blockSize &&
      ghost.x <= intersection.x
    )
      return DIRECTIONS.RIGHT;
    if (
      Math.abs(intersection.y - ghost.y) < this.blockSize &&
      ghost.x >= intersection.x
    )
      return DIRECTIONS.LEFT;
    return DIRECTIONS.UP;
  }

  /**
   * Check if a point is inside the ghost house area
   */
  private isInGhostHouse(x: number, y: number): boolean {
    return (
      x <= GHOST_HOUSE.RIGHT &&
      x >= GHOST_HOUSE.LEFT &&
      y <= GHOST_HOUSE.BOTTOM &&
      y > GHOST_HOUSE.TOP
    );
  }

  /**
   * Reset ghost positions to starting locations
   */
  resetGhostPositions(ghosts: Ghost[]): void {
    ghosts.forEach((ghost) => {
      let startPos: Point = GHOST_START_POSITIONS.PINK;
      if (ghost.texture.key === GHOST_TEXTURES.RED)
        startPos = GHOST_START_POSITIONS.RED;
      else if (ghost.texture.key === GHOST_TEXTURES.ORANGE)
        startPos = GHOST_START_POSITIONS.ORANGE as Point;
      else if (ghost.texture.key === GHOST_TEXTURES.BLUE)
        startPos = GHOST_START_POSITIONS.BLUE as Point;

      ghost.setPosition(startPos.x, startPos.y);
      ghost.setVelocity(0, 0);
      ghost.direction = DIRECTIONS.RIGHT;
      ghost.previousDirection = DIRECTIONS.RIGHT;
      ghost.nextIntersection = null;
      ghost.enteredMaze = false;
      ghost.path = [];
      ghost.hasBeenEaten = true;
      ghost.setTexture(ghost.originalTexture);

      if (ghost.blinkInterval) {
        clearInterval(ghost.blinkInterval);
        ghost.blinkInterval = undefined;
      }
      if (ghost.entryTimer) {
        clearTimeout(ghost.entryTimer);
        ghost.entryTimer = undefined;
      }
    });
  }

  /**
   * Reset ghosts (for game reset)
   */
  resetGhosts(ghosts: Ghost[]): void {
    this.resetGhostPositions(ghosts);
    this.currentMode = GAME_MODES.SCATTER;
    this.previousMode = GAME_MODES.SCATTER;
    this.ghostSpeed = this.speed * GAME_CONFIG.GHOST_SPEED_MULTIPLIER;

    // Clear existing mode timer
    if (this.modeTimer) {
      clearTimeout(this.modeTimer);
      this.modeTimer = null;
    }

    // Update paths for all ghosts BEFORE starting ghost entries
    ghosts.forEach((ghost) => {
      const target = this.getScatterTarget(ghost);
      this.updateGhostPath(ghost, target);
      ghost.direction = DIRECTIONS.LEFT;
    });

    // Start ghost entries with staggered timing
    this.startGhostEntries();

    // Set mode timer for scatter mode
    this.setModeTimer(this.scatterModeDuration);
  }

  /**
   * Respawn a ghost after being eaten
   * Shows ghost in ghost house first, then exits after delay
   */
  respawnGhost(ghost: Ghost): void {
    // Calculate center of ghost house
    const centerX = (GHOST_HOUSE.LEFT + GHOST_HOUSE.RIGHT) / 2;
    const centerY = (GHOST_HOUSE.TOP + GHOST_HOUSE.BOTTOM) / 2;

    // Position ghost in center of ghost house (visible immediately)
    ghost.setPosition(centerX, centerY);
    ghost.setTexture(ghost.originalTexture);

    ghost.setActive(true);
    ghost.setVisible(true);
    ghost.hasBeenEaten = true;
    ghost.enteredMaze = false;
    ghost.setVelocity(0, 0);

    // Clear blink interval if exists
    if (ghost.blinkInterval) {
      clearInterval(ghost.blinkInterval);
      ghost.blinkInterval = undefined;
    }

    // Wait in ghost house for 1 second so player can see it, then exit
    setTimeout(() => {
      // Enter the maze (sets enteredMaze = true and moves to entrance)
      this.enterMaze(ghost);

      // Update ghost path based on current mode
      const target =
        this.currentMode === GAME_MODES.CHASE
          ? this.getChaseTarget(ghost)
          : this.getScatterTarget(ghost);
      this.updateGhostPath(ghost, target);
    }, 1000); // Wait 1 second in ghost house before exiting
  }

  /**
   * Pause all timers (modeTimer, entryTimer, blinkInterval)
   */
  pauseTimers(): void {
    if (this.isPaused) return;
    this.isPaused = true;

    // Pause mode timer - calculate remaining time
    if (this.modeTimer) {
      const elapsed = Date.now() - this.modeTimerStartTime;
      this.modeTimerDuration = Math.max(0, this.modeTimerDuration - elapsed);
      clearTimeout(this.modeTimer);
      this.modeTimer = null;
    }

    // Pause ghost entry timers - calculate remaining time for each
    this.ghosts.forEach((ghost) => {
      if (ghost.blinkInterval) {
        clearInterval(ghost.blinkInterval);
        ghost.blinkInterval = undefined;
      }

      if (ghost.entryTimer) {
        // Calculate remaining time for entry timer
        const timerInfo = this.ghostEntryTimers.get(ghost);
        if (timerInfo) {
          const elapsed = Date.now() - timerInfo.startTime;
          timerInfo.duration = Math.max(0, timerInfo.duration - elapsed);
          timerInfo.startTime = Date.now(); // Update for resume calculation
        }

        clearTimeout(ghost.entryTimer);
        ghost.entryTimer = undefined;
      }
    });
  }

  /**
   * Resume all timers
   */
  resumeTimers(): void {
    if (!this.isPaused) return;
    this.isPaused = false;

    // Resume mode timer with remaining time
    if (this.modeTimerDuration > 0) {
      this.modeTimerStartTime = Date.now();
      this.modeTimer = setTimeout(() => {
        this.switchMode();
      }, this.modeTimerDuration);
    }

    // Resume ghost entry timers with remaining time
    this.ghosts.forEach((ghost) => {
      // Only resume entry timer if ghost hasn't entered maze yet
      if (!ghost.enteredMaze && this.ghostEntryTimers.has(ghost)) {
        const timerInfo = this.ghostEntryTimers.get(ghost)!;

        if (timerInfo.duration > 0) {
          // Update start time for accurate tracking
          timerInfo.startTime = Date.now();

          ghost.entryTimer = setTimeout(() => {
            this.enterMaze(ghost);
            // Remove from tracking once entry is complete
            this.ghostEntryTimers.delete(ghost);
          }, timerInfo.duration);
        } else {
          // Timer already expired, enter immediately
          this.enterMaze(ghost);
          this.ghostEntryTimers.delete(ghost);
        }
      }
    });

    // Note: Blink intervals are event-driven and will restart naturally
    // when scared mode is triggered again
  }

  /**
   * Cleanup timers
   */
  cleanup(): void {
    if (this.modeTimer) {
      clearTimeout(this.modeTimer);
      this.modeTimer = null;
    }
    this.modeTimerDuration = 0;
    this.modeTimerStartTime = 0;
    this.isPaused = false;
    this.ghostEntryTimers.clear();
    this.ghosts.forEach((ghost) => {
      if (ghost.blinkInterval) {
        clearInterval(ghost.blinkInterval);
      }
      if (ghost.entryTimer) {
        clearTimeout(ghost.entryTimer);
      }
    });
  }
}
